/**
 * Renders the API-driven past-auction grids (past catalogues, ended catalogues,
 * the past-auctions hub and the past phase of search).
 *
 * Exposed on window because a facet change swaps the whole grid container out for
 * freshly rendered markup, which arrives with an empty grid and a narrowed
 * data-past-product-ids -- facets.js calls this to repopulate it.
 */
window.staRenderPastAuctionGrids = function () {
  var grid;

  function clearPastCatalogPendingClass() {
    document.documentElement.classList.remove('past-catalog-pending');
    if (grid) {
      grid.classList.remove('loading');
      grid.removeAttribute('aria-busy');
    }
  }

  var PAST_COLLECTION_PATH = '/collections/past-catalogues';
  var qs = new URLSearchParams(window.location.search || '');
  var isPastCatalogParam = qs.get('past_catalog') === '1' || qs.get('past_catalog') === 'true';
  var durationRaw = String(qs.get('duration') || qs.get('status') || '').trim().toLowerCase()
    .replace(/\u2011/g, '-').replace(/\u2013/g, '-').replace(/\u2010/g, '-').replace(/\u2212/g, '-');
  var isDurationEnded = durationRaw === 'ended' || durationRaw === 'past';
  var collectionPathMatch = /^\/collections\/([^/]+)\/?$/.exec(window.location.pathname || '');
  var collectionPathHandle = collectionPathMatch ? String(collectionPathMatch[1] || '').toLowerCase() : '';
  var departmentHandlesMap = {
    'jewellery-fine-watches': true,
    'fine-decorative-art': true,
    'luxury-handbags-accessories': true,
    'antiques': true,
    'collectibles': true
  };
  var isDepartmentDurationCataloguePage = !!(departmentHandlesMap[collectionPathHandle] && (durationRaw === 'live' || isDurationEnded));
  if (isDepartmentDurationCataloguePage) {
    clearPastCatalogPendingClass();
    return;
  }
  var pathNorm = (window.location.pathname || '').replace(/\/+$/, '') || '/';
  var isSearchPage = /\/search$/i.test(pathNorm);
  var isCollectionPage = /^\/collections\/[^/]+\/?$/.test(window.location.pathname || '');
  var isPastCatalogCollectionPage = isCollectionPage && isPastCatalogParam;
  var isDurationEndedCollectionPage = isCollectionPage && isDurationEnded;

  // Shopify's own In stock box. On a past catalogue the rail pairs it with
  // custom.lot_sold=false (see facets.liquid), so both halves ride on this one flag.
  var inStockOnly = qs.get('filter.v.availability') === '1';
  var filtersApplied = /(^|&)filter\./.test((window.location.search || '').slice(1));

  var gridEnded = document.querySelector('#product-grid[data-ended-auction-collection="true"]');
  var isEndedCatalog = !!gridEnded || isPastCatalogCollectionPage || isDurationEndedCollectionPage;
  var isPastAll =
    location.pathname === PAST_COLLECTION_PATH ||
    location.pathname.indexOf(PAST_COLLECTION_PATH + '/') === 0;

  grid = gridEnded;
  if (!grid && isSearchPage) {
    grid =
      document.querySelector('.template-search #product-grid') ||
      document.getElementById('product-grid');
  }
  if (!grid && (isPastAll || isPastCatalogCollectionPage || isDurationEndedCollectionPage)) {
    grid =
      document.querySelector('.sta-collection-products__grid.collection') ||
      document.querySelector('.sta-collection-products__grid');
  }
  if (!grid) {
    clearPastCatalogPendingClass();
    return;
  }
  if (grid.getAttribute('data-department-duration-catalogues') === 'true') {
    clearPastCatalogPendingClass();
    return;
  }

  var seedCh = (grid.dataset.collectionHandle || '').trim();
  var seedCt = (grid.dataset.collectionTitle || '').trim();
  if (!seedCh && isPastCatalogCollectionPage) {
    var seedM = window.location.pathname.match(/^\/collections\/([^/]+)\/?$/);
    if (seedM && seedM[1]) seedCh = decodeURIComponent(seedM[1]);
  }
  if (!seedCt && isPastCatalogCollectionPage) {
    var seedH1 = document.querySelector('.sta-collection-hero__title') || document.querySelector('h1');
    if (seedH1) seedCt = String(seedH1.textContent || '').trim();
  }
  // An ended-catalogue grid always carries data-past-product-ids, and it is the server's
  // answer to the filters currently applied -- so an empty one means "nothing matched",
  // never "no answer given". The derived fallbacks below read the whole collection and
  // know nothing about filters, so reaching for one here would put every lot back on the
  // page, sold ones included, exactly when the shopper asked to exclude them.
  var hasServerIdList = grid.hasAttribute('data-past-product-ids');
  var seedIdSet = parsePastProductIdSet(grid);
  if (!seedIdSet && !hasServerIdList) {
    seedIdSet = deriveProductIdSetFromGrid(grid);
  }
  if (!hasServerIdList && (!seedIdSet || !seedIdSet.size) && isEndedCatalog) {
    seedIdSet = deriveProductIdSetFromCollectionContext(seedCh, seedCt);
  }

  // Search: match cards to the past API by path/handle/SKU/id — do not require urls-check seed ids
  // (data-href can differ from search result paths; collections.all pagination may omit products).

  if (isPastCatalogCollectionPage || isDurationEndedCollectionPage || isPastAll) {
    grid.classList.add('loading');
    grid.setAttribute('aria-busy', 'true');
    grid.innerHTML =
      '<div class="loading-overlay gradient" role="status" aria-live="polite" aria-label="Loading past auctions"></div>';
  }

  var API_BASE = (
    window.staAuctionsBase ? window.staAuctionsBase() : localStorage.getItem('sterlings_base_url') || ''
  ).replace(/\/+$/, '');

  function productNumericId(platformProductId) {
    if (platformProductId == null || platformProductId === '') return '';
    var s = String(platformProductId).trim();
    if (s.indexOf('/') !== -1) return (s.split('/').pop() || '').trim();
    return String(s.replace(/\D/g, '') || s);
  }

  /** Shopify storefront price from hidden urls-check list (theme.liquid data-store-price-cents). */
  function getStorePriceDollars(platformProductId) {
    var pid = productNumericId(platformProductId);
    if (!pid) return null;
    var li = document.querySelector(
      'ul.urls-check-cards-products li[data-product-id="' + pid + '"]'
    );
    if (!li) return null;
    var cents = parseInt(li.getAttribute('data-store-price-cents') || '', 10);
    return isFinite(cents) && cents > 0 ? cents / 100 : null;
  }

  function getStoreLotMeta(platformProductId) {
    var pid = productNumericId(platformProductId);
    if (!pid) return { lotSold: false, hasSellableStock: false };
    var li = document.querySelector(
      'ul.urls-check-cards-products li[data-product-id="' + pid + '"]'
    );
    if (!li) return { lotSold: false, hasSellableStock: false };
    return {
      lotSold: li.getAttribute('data-lot-sold') === 'true',
      hasSellableStock: li.getAttribute('data-has-sellable-stock') === 'true',
      buyNow: li.getAttribute('data-buy-now') === 'true',
    };
  }

  /** Post-auction lot still purchasable (no winner, not sold) — not an "ended" stamp lot. */
  function isPastCatalogueBuyNowOffer(hasWinner, isSold, platformProductId) {
    if (isSold || hasWinner) return false;
    var meta = getStoreLotMeta(platformProductId);
    if (meta.lotSold) return false;
    return true;
  }

  /** Featured image from theme.liquid urls-check map when the API omits product_image_url. */
  function getStoreProductImageUrl(platformProductId) {
    var pid = productNumericId(platformProductId);
    if (!pid) return '';
    var li = document.querySelector(
      'ul.urls-check-cards-products li[data-product-id="' + pid + '"]'
    );
    if (!li) return '';
    return String(li.getAttribute('data-product-image') || '').trim();
  }

  function themeNoPhotoUrl() {
    var probe = document.querySelector('img[src*="no-photo"]');
    return probe && probe.src ? probe.src : '';
  }

  /** A row the auction API has awarded. Shared so the In stock filter below and the
   *  card's own Sold badge can never reach different verdicts about the same lot. */
  function rowHasWinner(a) {
    var winnerRaw = a && a.winner_id != null ? a.winner_id : a ? a.winner_customer_id : null;
    return (
      winnerRaw != null &&
      String(winnerRaw).trim() !== '' &&
      String(winnerRaw).toLowerCase() !== 'null'
    );
  }

  function isPastLotSold(hasWinner, platformProductId) {
    var meta = getStoreLotMeta(platformProductId);
    if (meta.lotSold) return true;
    if (hasWinner && !meta.hasSellableStock) return true;
    return false;
  }

  function parsePastProductIdSet(el) {
    var raw = (el.dataset.pastProductIds || '').trim();
    if (!raw) return null;
    var set = new Set(
      raw
        .split(',')
        .map(function (s) {
          return String(s.trim());
        })
        .filter(Boolean)
    );
    return set.size ? set : null;
  }

  function deriveProductIdSetFromGrid(el) {
    if (!el) return null;
    var anchors = el.querySelectorAll('a[href*="/products/"]');
    if (!anchors || !anchors.length) return null;
    var set = new Set();
    for (var i = 0; i < anchors.length; i++) {
      var href = anchors[i].getAttribute('href') || '';
      if (!href) continue;
      var path = '';
      try {
        path = new URL(href, window.location.origin).pathname;
      } catch (e) {
        continue;
      }
      if (!path) continue;
      var li = document.querySelector('ul.urls-check-cards-products li[data-href="' + path + '"]');
      if (!li) continue;
      var pid = String(li.getAttribute('data-product-id') || '').trim();
      if (pid) set.add(pid);
    }
    return set.size ? set : null;
  }

  function deriveProductIdSetFromCollectionContext(handle, collectionTitle) {
    var h = String(handle || '').trim().toLowerCase();
    var t = String(collectionTitle || '').trim().toLowerCase();
    if (!h && !t) return null;
    var lis = document.querySelectorAll('ul.urls-check-cards-products li[data-product-id]');
    if (!lis || !lis.length) return null;
    var set = new Set();
    for (var i = 0; i < lis.length; i++) {
      var li = lis[i];
      var handlesRaw = li.getAttribute('data-collection-handles-of-products') || '';
      var titlesRaw = li.getAttribute('data-collection-of-products') || '';
      var handles = handlesRaw.split('+').map(function (s) {
        return s.trim().toLowerCase();
      }).filter(Boolean);
      var titles = titlesRaw.split('+').map(function (s) {
        return s.trim().toLowerCase();
      }).filter(Boolean);
      if ((h && handles.indexOf(h) !== -1) || (t && titles.indexOf(t) !== -1)) {
        var pid = String(li.getAttribute('data-product-id') || '').trim();
        if (pid) set.add(pid);
      }
    }
    return set.size ? set : null;
  }

  function rowBelongsToAuctionCollection(row, handle, collectionTitle) {
    var h = String(handle || '')
      .trim()
      .toLowerCase();
    var t = String(collectionTitle || '')
      .trim()
      .toLowerCase();
    if (!h && !t) return true;

    var a = row && row.attributes ? row.attributes : row || {};
    var primary = a.primary_catalog_collection;
    if (primary) {
      if (h && String(primary.handle || '').toLowerCase() === h) return true;
      if (t && String(primary.title || '').trim().toLowerCase() === t) return true;
    }
    var cols = a.shopify_collections || [];
    if (Array.isArray(cols)) {
      for (var i = 0; i < cols.length; i++) {
        var c = cols[i];
        if (h && String(c.handle || '').toLowerCase() === h) return true;
        if (t && String(c.title || '').trim().toLowerCase() === t) return true;
      }
    }

    var pid = productNumericId(a.platform_product_id);
    if (!pid) return false;
    var li = document.querySelector('ul.urls-check-cards-products li[data-product-id="' + pid + '"]');
    if (!li) return false;
    var handlesRaw = li.getAttribute('data-collection-handles-of-products') || '';
    var titlesRaw = li.getAttribute('data-collection-of-products') || '';
    var handles = handlesRaw.split('+').map(function (s) {
      return s.trim().toLowerCase();
    }).filter(Boolean);
    var titles = titlesRaw.split('+').map(function (s) {
      return s.trim().toLowerCase();
    }).filter(Boolean);
    if (h && handles.indexOf(h) !== -1) return true;
    if (t && titles.indexOf(t) !== -1) return true;
    return false;
  }

  /** Shopify product IDs present in urls-check (current catalog on the storefront). */
  function getStorefrontProductIdSet() {
    var s = new Set();
    document.querySelectorAll('ul.urls-check-cards-products li[data-product-id]').forEach(function (li) {
      var id = String(li.getAttribute('data-product-id') || '').trim();
      if (id) s.add(id);
    });
    return s;
  }

  var CARD_HTML =
    '\
<div class="sta-collection-products__product">\
  <div class="sta-product-card">\
    <div class="sta-product-card__header">\
      <a href="#" class="sta-product-card__ill">\
        <img src="" alt="" class="sta-product-card__img" loading="lazy">\
      </a>\
      <span class="sta-product-card__badge sta-product-badge _live" style="display:none;">\
        <span class="sta-product-badge__name">Live</span>\
      </span>\
      <button class="sta-product-card__to-wish sta-wish-btn" title="Add to wishlist" aria-label="Add to wishlist"></button>\
    </div>\
    <div class="sta-product-card__body">\
      <h3 class="sta-product-card__title"><a href="#">Auction Item</a></h3>\
      <div class="sta-product-card__auction"><span data-auction-label>Past Auction</span></div>\
    </div>\
    <div class="sta-product-card__info">\
      <div class="sta-product-card__info-row sta-product-card__info-row--no-estimate">\
        <div class="sta-product-specs">\
        <div class="sta-product-spec">\
          <span class="sta-product-spec__ico"></span>\
          <div class="sta-product-spec__body">\
            <div class="sta-product-spec__title">SKU: —</div>\
          </div>\
        </div>\
        <div class="sta-product-spec">\
          <svg class="svg-ico sta-product-spec__ico">\
            <use xlink:href="//sterlingauctions.com/cdn/shop/t/3/assets/sprite.svg?v=122477482122922329031736255626#stopwatch"></use>\
          </svg>\
          <div class="sta-product-spec__body">\
            <div class="sta-product-spec__title">Ended</div>\
            <p></p>\
          </div>\
        </div>\
        </div>\
        <div class="sta-product-card__estimate-slot" data-price-estimate-slot hidden></div>\
      </div>\
    </div>\
    <div class="sta-product-card__footer">\
      <div class="sta-product-card__price">\
        <div class="sta-product-spec__body sta-product-price">\
          <div class="sta-product-price__title"></div>\
          <div class="sta-product-price__price"></div>\
        </div>\
        <div class="sta-product-card__bids">\
          <svg class="svg-ico sta-product-card__bids-ico"><use xlink:href="//sterlingauctions.com/cdn/shop/t/3/assets/sprite.svg?v=122477482122922329031736255626#auction-hammer"></use></svg>\
          <div class="sta-product-card__bids-body">\
            <div class="sta-product-card__bids-count">0</div>\
            <div>Bids</div>\
          </div>\
        </div>\
      </div>\
      <a href="#" class="sta-btn _full center" style="display:none;">Buy Now</a>\
    </div>\
  </div>\
</div>';

  function buildPastAuctionCard(row) {
    var a = row.attributes || row;

    var tmp = document.createElement('div');
    tmp.innerHTML = CARD_HTML.trim();
    var card = tmp.firstElementChild;

    var name = a.auction_name || a.product_name || a.name || 'Auction Item';
    var sku = a.product_sku || '';
    var img =
      a.product_image_url ||
      a.image_url ||
      getStoreProductImageUrl(a.platform_product_id) ||
      themeNoPhotoUrl() ||
      '';
    var url = a.product_url || '#';
    var aid = (row && row.id) || a.auction_id || a.id;
    var pathName = '';

    if (url && url !== '#') {
      var isAbs = /^https?:\/\//i.test(url);
      var isProductsPath = /^\/?products\//i.test(url);
      if (!isAbs) {
        url = isProductsPath
          ? url.startsWith('/')
            ? url
            : '/' + url
          : '/products/' + url.replace(/^\/+/, '');
        url = new URL(url, window.location.origin).href;
      }
      try {
        var u = new URL(url);
        pathName = u.pathname;
        if (aid != null) u.searchParams.set('auction_id', String(aid));
        url = u.toString();
      } catch (e) {}
    }

    var hasWinner = rowHasWinner(a);

    var displayPrice =
      a.current_price != null
        ? a.current_price
        : a.starting_price != null
          ? a.starting_price
          : a.initial_price != null
            ? a.initial_price
            : 0;
    if (displayPrice === 0 || displayPrice == null) {
      if (a.final_price != null) displayPrice = a.final_price;
      else if (a.hammer_price != null) displayPrice = a.hammer_price;
    }

    var storePriceDollars = !hasWinner ? getStorePriceDollars(a.platform_product_id) : null;
    if (!hasWinner && storePriceDollars != null) {
      displayPrice = storePriceDollars;
    }

    var isSold = isPastLotSold(hasWinner, a.platform_product_id);

    var bids = a.bid_count || a.bids_count || 0;

    var isBuyNowOffer = isPastCatalogueBuyNowOffer(
      hasWinner,
      isSold,
      a.platform_product_id
    );

    var link = card.querySelector('.sta-product-card__ill');
    if (link) link.href = url;
    var imgEl = card.querySelector('.sta-product-card__img');
    if (imgEl) {
      if (img) {
        imgEl.src = img;
        imgEl.alt = name;
        imgEl.removeAttribute('srcset');
      } else {
        imgEl.removeAttribute('src');
        imgEl.alt = name;
      }
    }

    var badge = card.querySelector('.sta-product-card__badge');
    if (badge) {
      var bname = badge.querySelector('.sta-product-badge__name');
      badge.classList.remove('_live', '_sold');
      if (isBuyNowOffer) {
        // Ended with no winner and still purchasable (the price-boost → Buy Now
        // flow). Not sold, so it gets no badge rather than a misleading one.
        badge.style.display = 'none';
      } else {
        // Every other past lot reads "Sold", including one whose winner has not
        // checked out yet: the lot is spoken for either way, and only the parent
        // catalogue card is allowed to say "Past".
        badge.style.display = '';
        badge.classList.add('_sold');
        if (bname) bname.textContent = 'Sold';
      }
    }

    var titleA = card.querySelector('.sta-product-card__title a');
    if (titleA) {
      titleA.textContent = name;
      titleA.href = url;
    }

    var skuTitle = card.querySelector('.sta-product-spec__title');
    if (skuTitle) skuTitle.textContent = sku ? 'SKU: ' + sku : 'SKU: —';

    var bodies = card.querySelectorAll('.sta-product-spec__body');
    var endsBody = bodies[1] || bodies[0];
    if (endsBody) {
      var endsTitle = endsBody.querySelector('.sta-product-spec__title');
      if (endsTitle) {
        if (isBuyNowOffer) endsTitle.textContent = 'Buy now';
        else if (isSold) endsTitle.textContent = 'Sold';
        else endsTitle.textContent = 'Ended';
      }
      var p = endsBody.querySelector('p');
      if (p) p.textContent = '';
    }

    var noWinnerNoPriceYet = !hasWinner && !isSold && displayPrice === 0 && storePriceDollars == null;

    var priceWrap = card.querySelector('.sta-product-card__price .sta-product-price');
    if (priceWrap) {
      priceWrap.classList.remove('sta-product-price--hammer', 'sta-product-price--past');
      if (hasWinner) {
        priceWrap.classList.add('sta-product-price--hammer');
      }
      priceWrap.style.display = noWinnerNoPriceYet ? 'none' : '';
    }
    var priceTitle = card.querySelector('.sta-product-price__title');
    if (priceTitle) {
      if (isSold) {
        priceTitle.textContent = hasWinner ? 'Hammer Price:' : 'Sold for:';
      } else {
        priceTitle.textContent = hasWinner ? 'Hammer Price:' : 'Buy now price:';
      }
    }
    var priceEl = card.querySelector('.sta-product-price__price');
    if (priceEl) {
      priceEl.classList.toggle('sta-product-price__price--hammer', hasWinner);
      setMoneyPrice(priceEl, displayPrice);
    }

    var bidsEl = card.querySelector('.sta-product-card__bids-count');
    if (bidsEl) bidsEl.textContent = bids;

    var bidsRow = card.querySelector('.sta-product-card__bids');
    if (bidsRow) {
      bidsRow.style.display = !hasWinner && bids === 0 ? 'none' : '';
    }

    var cta = card.querySelector('.sta-product-card__footer a.sta-btn') || card.querySelector('.sta-btn');
    if (cta) {
      if (isSold || hasWinner) {
        // A sold lot cannot be bought, but it still has a page worth reading, so
        // it gets the Archive card's "Lot Details" button rather than no button at
        // all — same label and same _primary-lightest styling as
        // lot-card-product.liquid, so a sold card looks the same wherever it lands.
        cta.style.display = '';
        cta.textContent = 'Lot Details';
        cta.href = url;
        cta.classList.remove('_secondary');
        cta.classList.add('_primary-lightest', '_full', 'center');
      } else {
        cta.style.display = '';
        cta.textContent = 'Buy Now';
        cta.href = url;
        cta.classList.remove('_secondary', '_primary-lightest');
        if (!cta.classList.contains('_full')) cta.classList.add('_full');
      }
    }

    if (window.staApplyPriceEstimateToCard) {
      var estPid = window.staProductIdFromAuctionAttrs
        ? window.staProductIdFromAuctionAttrs(a, null)
        : productNumericId(a.platform_product_id);
      window.staApplyPriceEstimateToCard(card, estPid);
    }

    return { element: card, pathname: pathName };
  }

  function pickArray(p) {
    return Array.isArray(p && p.items)
      ? p.items
      : Array.isArray(p && p.data)
        ? p.data
        : Array.isArray(p)
          ? p
          : Array.isArray(p && p.data && p.data.data)
            ? p.data.data
            : [];
  }
  function attr(it, k) {
    return it && it.attributes ? it.attributes[k] : it ? it[k] : undefined;
  }
  function setMoneyPrice(el, dollars) {
    if (!el) return;
    if (window.staSetMoneyEl) window.staSetMoneyEl(el, dollars);
    else if (window.staFormatMoney) el.innerHTML = window.staFormatMoney(dollars);
  }
  function syncHeroLotCount(total) {
    var n = total > 0 ? total : 0;
    var text = n === 1 ? '1 Lot' : n + ' Lots';
    var heroById = document.getElementById('sta-hero-catalogue-lots-label');
    if (heroById && heroById.getAttribute('data-sta-past-all-hero') === 'true') return;
    if (heroById) heroById.textContent = text;
    document.querySelectorAll('.sta-collection-hero [data-collection-count]').forEach(function (el) {
      if (el.getAttribute('data-sta-past-all-hero') === 'true') return;
      el.textContent = text;
    });
  }

  /** /collections/past-catalogues — hero counts unique past auctions for rows tied to current storefront product ids (urls-check), not path/SKU matches on recreated products. */
  function pastRowAuctionDedupeKey(row) {
    var a = row.attributes || row;
    if (a.auction_id != null && String(a.auction_id).trim() !== '') return 'a:' + String(a.auction_id).trim();
    if (row && row.id != null && String(row.id).trim() !== '') return 'r:' + String(row.id).trim();
    if (a.id != null && String(a.id).trim() !== '') return 'i:' + String(a.id).trim();
    var pid = productNumericId(a.platform_product_id);
    return pid ? 'p:' + pid : '';
  }
  function countUniquePastAuctionsInStore(rows, storefrontIdSet) {
    var idSet = storefrontIdSet || getStorefrontProductIdSet();
    var seen = new Set();
    for (var i = 0; i < rows.length; i++) {
      var pid = productNumericId(attr(rows[i], 'platform_product_id'));
      if (!pid || !idSet.has(String(pid))) continue;
      var k = pastRowAuctionDedupeKey(rows[i]);
      if (!k) continue;
      seen.add(k);
    }
    return seen.size;
  }
  function pastRowEndMsForPastAll(row) {
    var a = row.attributes || row;
    var t = Date.parse(a.end_date || a.end_at || a.ended_at || a.closed_at || '');
    return isFinite(t) ? t : 0;
  }
  function pastRowAuctionIdNumForPastAll(row) {
    var a = row.attributes || row;
    var raw =
      row && row.id != null && row.id !== ''
        ? row.id
        : a.auction_id != null && a.auction_id !== ''
          ? a.auction_id
          : a.id;
    var n = parseInt(String(raw).replace(/\D/g, ''), 10);
    return isFinite(n) ? n : 0;
  }
  function pastRowShouldReplaceForPastAll(incumbent, candidate) {
    if (!incumbent) return true;
    var cEnd = pastRowEndMsForPastAll(candidate);
    var iEnd = pastRowEndMsForPastAll(incumbent);
    if (cEnd !== iEnd) return cEnd > iEnd;
    return pastRowAuctionIdNumForPastAll(candidate) > pastRowAuctionIdNumForPastAll(incumbent);
  }
  function dedupePastRowsByPlatformProductId(rows) {
    var byPid = {};
    for (var i = 0; i < rows.length; i++) {
      var row = rows[i];
      var pid = productNumericId(attr(row, 'platform_product_id'));
      if (!pid) continue;
      var key = String(pid);
      if (pastRowShouldReplaceForPastAll(byPid[key], row)) byPid[key] = row;
    }
    return Object.keys(byPid).map(function (k) {
      return byPid[k];
    });
  }
  function filterPastRowsToStorefrontProductIds(rows, idSet) {
    return rows.filter(function (row) {
      var pid = productNumericId(attr(row, 'platform_product_id'));
      return pid && idSet.has(String(pid));
    });
  }
  function syncHeroPastAuctionCount(total) {
    var n = total < 0 ? 0 : total;
    var text = n === 1 ? '1 Past Auction' : n + ' Past Auctions';
    var heroById = document.getElementById('sta-hero-catalogue-lots-label');
    if (heroById) heroById.textContent = text;
    document.querySelectorAll('.sta-collection-hero [data-sta-past-all-hero="true"]').forEach(function (el) {
      el.textContent = text;
    });
  }

  /**
   * Which Availability options this catalogue can actually satisfy.
   *
   * Shopify decides that from custom.lot_sold, so one lot carrying a stale metafield is
   * enough to keep offering "In stock" on a catalogue where nothing is buyable -- the
   * shopper ticks it and gets an empty grid. The API rows know better, but they only
   * describe the whole catalogue while nothing is narrowing them, so the verdict is taken
   * on the unfiltered view and kept for the rest of the visit. It survives facet renders
   * on window, since each of those re-enters this function from the top.
   */
  window.staPastAvailabilityCounts = window.staPastAvailabilityCounts || {};

  function availabilityCacheKey() {
    return String(seedCh || collectionPathHandle || window.location.pathname || '').toLowerCase();
  }

  function rememberAvailabilityCounts(rows) {
    var buyNow = 0;
    for (var i = 0; i < rows.length; i++) {
      var a = rows[i].attributes || rows[i];
      var won = rowHasWinner(a);
      if (isPastCatalogueBuyNowOffer(won, isPastLotSold(won, a.platform_product_id), a.platform_product_id)) {
        buyNow++;
      }
    }
    window.staPastAvailabilityCounts[availabilityCacheKey()] = {
      buyNow: buyNow,
      sold: rows.length - buyNow,
    };
  }

  /** A ticked option is never hidden -- the shopper has to be able to clear it. */
  function hideEmptyAvailabilityOptions() {
    if (!isEndedCatalog) return;
    var counts = window.staPastAvailabilityCounts[availabilityCacheKey()];
    if (!counts) return;

    var hideWhenEmpty = function (selector, count) {
      if (count > 0) return;
      var inputs = document.querySelectorAll(selector);
      for (var i = 0; i < inputs.length; i++) {
        if (inputs[i].checked) continue;
        var item = inputs[i].closest('li');
        // display rather than [hidden]: .facets__item sets display:flex, which wins.
        if (item) item.style.display = 'none';
      }
    };

    hideWhenEmpty('input[name="filter.v.availability"][value="1"]', counts.buyNow);
    hideWhenEmpty('input[data-sta-sold-filter]', counts.sold);
  }

  // An empty grid reads very differently depending on why it is empty: a catalogue with
  // nothing in it yet, or a filter the shopper can undo.
  function showEmptyState() {
    hideEmptyAvailabilityOptions();
    grid.innerHTML = '';
    var empty = document.createElement('div');
    empty.style.padding = '12px';
    if (filtersApplied) {
      empty.textContent = 'No lots match these filters.';
    } else {
      empty.textContent = isEndedCatalog ? 'No past auctions in this catalogue yet.' : 'No past auctions yet.';
    }
    grid.appendChild(empty);
    if (isPastAll) syncHeroPastAuctionCount(0);
    else syncHeroLotCount(0);
    clearPastCatalogPendingClass();
  }

  // Nothing survived the filters, so there is nothing to ask the API for. Without this
  // the fetch runs unscoped and every row belonging to the catalogue comes back.
  hideEmptyAvailabilityOptions();

  if (isEndedCatalog && hasServerIdList && (!seedIdSet || !seedIdSet.size)) {
    showEmptyState();
    return;
  }

  var headers = window.staAuctionHeaders ? window.staAuctionHeaders() : { Accept: 'application/json' };

  var endedCatalogProductIds = null;
  if (isEndedCatalog && !isPastAll && !isSearchPage && seedIdSet && seedIdSet.size) {
    endedCatalogProductIds = Array.from(seedIdSet);
  }

  var pastFetch;
  if (window.staFetchPastAuctionRows) {
    pastFetch = window.staFetchPastAuctionRows(
      endedCatalogProductIds ? { productIds: endedCatalogProductIds } : undefined
    );
  } else if (endedCatalogProductIds && window.staFetchAuctionsByProductIds) {
    pastFetch = window.staFetchAuctionsByProductIds(endedCatalogProductIds).then(function (payload) {
      var rows = window.staPickAuctionArray
        ? window.staPickAuctionArray(payload)
        : payload.data || [];
      if (window.staFilterPastAuctionRows) rows = window.staFilterPastAuctionRows(rows);
      return { data: rows };
    });
  } else if (window.staFetchPastAuctionsPayload) {
    pastFetch = window.staFetchPastAuctionsPayload();
  } else {
    pastFetch = fetch(API_BASE + '/api/auctions', { headers: headers, mode: 'cors', credentials: 'omit' })
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.json();
      })
      .then(function (payload) {
        var rows = window.staFilterPastAuctionRows
          ? window.staFilterPastAuctionRows(
              window.staPickAuctionArray ? window.staPickAuctionArray(payload) : payload.data || []
            )
          : payload.data || [];
        return { data: rows };
      });
  }

  pastFetch
    .then(function (payload) {
      var list = pickArray(payload);
      var items = list.slice();

      if (isEndedCatalog) {
        var idSet = seedIdSet;
        var ch = seedCh;
        var ct = seedCt;
        if (!ch && isPastCatalogCollectionPage) {
          var m = window.location.pathname.match(/^\/collections\/([^/]+)\/?$/);
          if (m && m[1]) ch = decodeURIComponent(m[1]);
        }
        if (!ct && isPastCatalogCollectionPage) {
          var h1 = document.querySelector('.sta-collection-hero__title') || document.querySelector('h1');
          if (h1) ct = String(h1.textContent || '').trim();
        }
        if (!endedCatalogProductIds) {
          items = items.filter(function (row) {
            if (idSet && idSet.size) {
              var pid = productNumericId(attr(row, 'platform_product_id'));
              return pid && idSet.has(String(pid));
            }
            return rowBelongsToAuctionCollection(row, ch, ct);
          });
        }

        // Unfiltered, so these rows are the whole catalogue and can settle which
        // Availability options are worth offering at all.
        if (!filtersApplied) rememberAvailabilityCounts(items);

        // In stock has to mean "still buyable" here, and Shopify can only enforce that
        // through custom.lot_sold -- which a lot can carry stale. A lot won at the hammer
        // whose metafield was never updated passes the filter server-side and arrives
        // holding a winner, which is why one could show a Sold badge under In stock.
        //
        // Kept in the shopper's results only if the card would render it as a Buy Now
        // offer -- the same predicate that decides the badge, so the rail and the card can
        // never disagree about a lot. Note that is stricter than isPastLotSold on its own:
        // a won lot keeps its inventory until the winner pays, and the badge calls that
        // Sold even though the stock is still there. Only ever narrows the set; nothing
        // the shopper asked to see gets added back.
        if (inStockOnly) {
          items = items.filter(function (row) {
            var a = row.attributes || row;
            var rowWon = rowHasWinner(a);
            return isPastCatalogueBuyNowOffer(rowWon, isPastLotSold(rowWon, a.platform_product_id), a.platform_product_id);
          });
        }
      }

      var storefrontIds = null;
      if (isPastAll) {
        storefrontIds = getStorefrontProductIdSet();
        items = filterPastRowsToStorefrontProductIds(items, storefrontIds);
        items = dedupePastRowsByPlatformProductId(items);
        items.sort(function (a, b) {
          return pastRowEndMsForPastAll(b) - pastRowEndMsForPastAll(a);
        });
      }

      if (isSearchPage) {
        if (!items.length) {
          clearPastCatalogPendingClass();
          return;
        }

        function normalizeProductPath(u) {
          if (!u) return '';
          var s = String(u);
          try {
            var parsed = /^https?:\/\//i.test(s) ? new URL(s) : new URL(s, window.location.origin);
            return (parsed.pathname.replace(/\/+$/, '') || '/');
          } catch (e) {
            return ((s.split('?')[0].split('#')[0]).replace(/\/+$/, '') || '/');
          }
        }
        /** Align Liquid product.url (sometimes /locale/products/...) with search card paths (/products/...). */
        function stripLeadingLocaleSegment(pathN) {
          if (!pathN) return '';
          var p = pathN.replace(/\/+$/, '') || '/';
          var m = p.match(/^\/(?:[a-z]{2}(?:-[a-z]{2})?)\/(.*)$/i);
          if (m && m[1]) return ('/' + m[1]).replace(/\/+$/, '') || '/';
          return p;
        }
        function canonicalProductPath(u) {
          return stripLeadingLocaleSegment(normalizeProductPath(u));
        }
        /** Same platform_product_id can appear in multiple past API rows. Prefer the most recently ended lot. */
        function pastRowEndMs(row) {
          var a = row.attributes || row;
          var t = Date.parse(a.end_date || a.end_at || a.ended_at || a.closed_at || '');
          return isFinite(t) ? t : 0;
        }
        function pastRowAuctionIdNum(row) {
          var a = row.attributes || row;
          var raw =
            row && row.id != null && row.id !== ''
              ? row.id
              : a.auction_id != null && a.auction_id !== ''
                ? a.auction_id
                : a.id;
          var n = parseInt(String(raw).replace(/\D/g, ''), 10);
          return isFinite(n) ? n : 0;
        }
        function pastRowShouldReplace(incumbent, candidate) {
          if (!incumbent) return true;
          var cEnd = pastRowEndMs(candidate);
          var iEnd = pastRowEndMs(incumbent);
          if (cEnd !== iEnd) return cEnd > iEnd;
          return pastRowAuctionIdNum(candidate) > pastRowAuctionIdNum(incumbent);
        }
        function putPastRowMap(map, key, row) {
          if (!key || !row) return;
          if (pastRowShouldReplace(map[key], row)) map[key] = row;
        }

        // Search: match only by Shopify product id — same handle/SKU can exist on deleted+recreated products with different platform_product_id.
        var byProductId = {};
        items.forEach(function (row) {
          var rowPid = productNumericId(attr(row, 'platform_product_id'));
          if (rowPid) putPastRowMap(byProductId, String(rowPid), row);
        });

        var idByPath = {};
        document.querySelectorAll('ul.urls-check-cards-products li[data-href][data-product-id]').forEach(function (li) {
          var href = li.getAttribute('data-href') || '';
          var mapPid = productNumericId(li.getAttribute('data-product-id'));
          if (!mapPid) return;
          var cp = canonicalProductPath(href);
          if (cp) idByPath[cp] = String(mapPid);
        });

        grid.querySelectorAll('.sta-product-card__title a').forEach(function (aEl) {
          if (!aEl) return;
          var href = aEl.getAttribute('href') || '';
          if (href.indexOf('auction_id=') !== -1) return;

          var pp = canonicalProductPath(href);
          var pid = idByPath[pp];
          if (!pid) return;
          var row = byProductId[String(pid)];
          if (!row) return;

          var oldWrapper = aEl.closest('.sta-collection-products__product');
          if (!oldWrapper) return;

          oldWrapper.replaceWith(buildPastAuctionCard(row).element);
        });

        clearPastCatalogPendingClass();
        return;
      }

      grid.innerHTML = '';
      if (!items.length) {
        showEmptyState();
        return;
      }

      hideEmptyAvailabilityOptions();

      items.forEach(function (row) {
        var built = buildPastAuctionCard(row);
        grid.appendChild(built.element);
      });

      if (isPastAll) syncHeroPastAuctionCount(countUniquePastAuctionsInStore(items, storefrontIds));
      else syncHeroLotCount(items.length);
      clearPastCatalogPendingClass();
    })
    .catch(function (err) {
      console.error('[auction-grid/past] fetch failed:', err);
      clearPastCatalogPendingClass();
    })
    .finally(function () {
      if (isSearchPage && typeof window.staMarkSearchAuctionPhase === 'function') {
        window.staMarkSearchAuctionPhase('past');
      }
    });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', window.staRenderPastAuctionGrids);
} else {
  window.staRenderPastAuctionGrids();
}
